import { createClient } from '@supabase/supabase-js';

// Supabase project details
// Provide fallbacks to prevent crash if env vars are missing
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key';

// Check if we're in a development environment and log a warning if using placeholder values
if (import.meta.env.DEV || !import.meta.env.VITE_SUPABASE_URL) {
  console.log('Initializing Supabase with URL:', supabaseUrl);
  if (!import.meta.env.VITE_SUPABASE_URL) {
    console.warn('VITE_SUPABASE_URL is missing. Using placeholder URL. Supabase features will not work.');
  }
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    debug: import.meta.env.DEV // Enable auth debugging in development
  }
});

// Export a function to check connection status
export const checkSupabaseConnection = async () => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('count')
      .limit(1);

    if (error) throw error;
    return { connected: true, data };
  } catch (error) {
    console.error('Supabase connection error:', error);
    return { connected: false, error };
  }
};

// Helper function to get available tools
export const getAvailableTools = async () => {
  try {
    const { data, error } = await supabase
      .from('tools')
      .select('*')
      .eq('visibility', 'visible')
      .order('type', { ascending: true })
      .order('name', { ascending: true });

    if (error) throw error;
    return { tools: data || [], error: null };
  } catch (err) {
    console.error('Error fetching available tools:', err);
    return { tools: [], error: err };
  }
};

// Helper function to group tools by type
export const getToolsGroupedByType = async () => {
  try {
    const { tools, error } = await getAvailableTools();
    
    if (error) throw error;
    
    // Group tools by their type
    const grouped = tools.reduce((acc, tool) => {
      // Use "Other" as the default category if type is missing
      const type = tool.type || "Other";
      
      if (!acc[type]) {
        acc[type] = [];
      }
      
      acc[type].push(tool);
      return acc;
    }, {});
    
    return { 
      toolGroups: grouped, 
      groupNames: Object.keys(grouped).sort((a, b) => {
        // Put "Other" at the end
        if (a === "Other") return 1;
        if (b === "Other") return -1;
        return a.localeCompare(b);
      }),
      error: null 
    };
  } catch (err) {
    console.error('Error grouping tools by type:', err);
    return { toolGroups: {}, groupNames: [], error: err };
  }
};